import { Search } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useSkuSearch, type SkuSearchItem } from "../api/use-sku-search";

interface BaseProductPickerProps {
  onSelect: (item: SkuSearchItem) => void;
  disabled?: boolean;
  /** SKUs already picked — hidden from the suggestion list. */
  excludeSkus?: string[];
  placeholder?: string;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

const DEBOUNCE_MS = 250;

/**
 * Searchable combobox over existing base products (the legacy "เลือกสินค้า"
 * picker). Type a product name/SKU → debounced query to `/v1/stock?search=` →
 * dropdown of matches. Click / Enter selects a row and clears the input.
 */
export function BaseProductPicker({
  onSelect,
  disabled = false,
  excludeSkus = [],
  placeholder,
}: BaseProductPickerProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce the term that drives the query.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term]);

  const { data, isFetching } = useSkuSearch(debounced);

  const excluded = new Set(excludeSkus);
  const options = (data ?? []).filter((item) => !excluded.has(item.sku));
  // Clamp the highlight into range at render time (the option list shrinks as
  // results change / items are excluded) so we never index past the end.
  const activeIndex = options.length === 0 ? 0 : Math.min(highlight, options.length - 1);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function choose(item: SkuSearchItem): void {
    onSelect(item);
    setTerm("");
    setDebounced("");
    setOpen(false);
    setHighlight(0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight(Math.min(activeIndex + 1, Math.max(options.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(Math.max(activeIndex - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      const item = options[activeIndex];
      if (item) {
        e.preventDefault();
        choose(item);
      }
    }
  }

  // Show the dropdown whenever the picker is open (focused), even with an empty
  // term — that surfaces the initial product list. Typing filters it.
  const showDropdown = open;

  return (
    <div ref={containerRef} className="relative">
      <Search
        className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
        aria-hidden
      />
      <input
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="base-product-listbox"
        aria-autocomplete="list"
        data-testid="base-product-picker"
        disabled={disabled}
        value={term}
        placeholder={placeholder ?? t("modal.create_sku_bundle.picker_placeholder")}
        className={FIELD_CLASS}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />

      {showDropdown && (
        <div
          role="listbox"
          id="base-product-listbox"
          data-testid="base-product-listbox"
          className="absolute z-[90] mt-1 max-h-60 w-full overflow-y-auto rounded-[var(--control-r)] border border-[var(--premium-border)] bg-white py-1 shadow-[var(--sh-pop)]"
        >
          {isFetching && options.length === 0 ? (
            <p
              className="px-3 py-2 text-[12.5px] text-[var(--text-subtle)]"
              data-testid="base-product-searching"
            >
              {t("modal.create_sku_bundle.picker_searching")}
            </p>
          ) : options.length === 0 ? (
            <p
              className="px-3 py-2 text-[12.5px] text-[var(--text-subtle)]"
              data-testid="base-product-empty"
            >
              {t("modal.create_sku_bundle.picker_no_results")}
            </p>
          ) : (
            options.map((item, index) => (
              <button
                key={item.sku}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                data-testid={`base-product-option-${item.sku}`}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(item)}
                className={[
                  "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition",
                  index === activeIndex ? "bg-[var(--canvas)]" : "hover:bg-[var(--canvas)]",
                ].join(" ")}
              >
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {item.product_name || item.sku}
                </span>
                <span className="sku text-xs text-[var(--text-subtle)]">{item.sku}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
